import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationLink } from './Router';
import LanguageSwitcher from './LanguageSwitcher';
import { TwitterIcon, InstagramIcon } from './Icon';
import ThemeToggle from './ThemeToggle';

interface NavigationProps {
  variant?: 'header' | 'footer' | 'sidebar';
  className?: string;
}

const Navigation: React.FC<NavigationProps> = ({
  variant = 'header',
  className = ''
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const cityPath = lang === 'fr' ? 'ville' : lang === 'nl' ? 'stad' : 'city';
  const chainsPath = lang === 'fr' ? 'chaines' : lang === 'nl' ? 'ketens' : 'chains';
  const backlogPath = lang === 'fr' ? 'retard-inspection' : lang === 'nl' ? 'inspectie-achterstand' : 'inspection-backlog';
  const [isExpanded, setIsExpanded] = useState(false);
  
  const navigationItems = [
    { path: '/', label: t('navigation.home') },
    { path: '/report-food-poisoning', label: t('navigation.reportPoisoning') || 'Report Food Poisoning' },
    { path: '/ultimate-guide', label: t('navigation.guide') || 'Reporting Guide' },
    { path: '/consumer-rights', label: t('navigation.rights') || 'Your Rights' },
    { path: '/managing-symptoms', label: t('navigation.symptoms') || 'Symptoms' },
    { path: '/faq', label: t('navigation.faq') },
    { path: '/about', label: t('navigation.about') },
  ];

  if (variant === 'header') {
    return (
      <nav className={`bg-white border-b border-gray-200 ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo/Brand */}
            <div className="flex items-center">
              <NavigationLink 
                to="/" 
                className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors"
              >
                Kauwen of Spauwen
              </NavigationLink>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigationItems.slice(1).map((item) => (
                <NavigationLink
                  key={item.path}
                  to={item.path}
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors"
                  activeClassName="text-blue-600 border-b-2 border-blue-600"
                >
                  {item.label}
                </NavigationLink>
              ))}
              <LanguageSwitcher variant="toggle" showFlags={true} showText={false} />
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-2">
              <LanguageSwitcher variant="toggle" showFlags={true} showText={false} />
              <button
                type="button"
                className="text-gray-600 hover:text-gray-900 focus:outline-none focus:text-gray-900"
                aria-label="Toggle menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  if (variant === 'footer') {
    return (
      <footer
        className={`bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white pb-safe-mobile ${className}`}
      >
        {/* Collapsible Content */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {/* Brand Column */}
              <div className="col-span-1 md:col-span-2">
                <h3 className="text-lg font-bold mb-4">Kauwen of Spauwen</h3>
                <p className="text-gray-500 dark:text-gray-300 mb-4">
                  {t('homepage.description')}
                </p>
              </div>

              {/* Navigation Column */}
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">
                  {t('navigation.navigation')}
                </h4>
                <ul className="space-y-2">
                  {navigationItems.map((item) => (
                    <li key={item.path}>
                      <NavigationLink
                        to={item.path}
                        className="text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                      >
                        {item.label}
                      </NavigationLink>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Browse Cities Column */}
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-blue-400">
                  {t('navigation.cities', 'Cities')}
                </h4>
                <ul className="space-y-2">
                  {[
                    { name: 'Brussel', slug: 'brussel' },
                    { name: 'Antwerpen', slug: 'antwerpen' },
                    { name: 'Gent', slug: 'gent' },
                    { name: 'Brugge', slug: 'brugge' },
                    { name: 'Leuven', slug: 'leuven' },
                    { name: 'Mechelen', slug: 'mechelen' },
                    { name: 'Hasselt', slug: 'hasselt' },
                    { name: 'Kortrijk', slug: 'kortrijk' },
                    { name: 'Oostende', slug: 'oostende' },
                    { name: 'Namen', slug: 'namen' },
                  ].map(city => (
                    <li key={city.slug}>
                      <a href={`/${lang}/${cityPath}/${city.slug}`} className="text-gray-500 dark:text-gray-300 hover:text-blue-400 text-sm transition-colors">
                        {city.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Provinces Column */}
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-blue-400">
                  {t('navigation.provinces', 'Provinces')}
                </h4>
                <ul className="space-y-2">
                  {[
                    { name: 'Antwerpen', slug: 'antwerpen' },
                    { name: 'Brussel', slug: 'brussel' },
                    { name: 'Henegouwen', slug: 'henegouwen' },
                    { name: 'Limburg', slug: 'limburg' },
                    { name: 'Luik', slug: 'luik' },
                    { name: 'Luxemburg', slug: 'luxemburg' },
                    { name: 'Namen', slug: 'namen' },
                    { name: 'Oost-Vlaanderen', slug: 'oost-vlaanderen' },
                    { name: 'Vlaams-Brabant', slug: 'vlaams-brabant' },
                    { name: 'Waals-Brabant', slug: 'waals-brabant' },
                    { name: 'West-Vlaanderen', slug: 'west-vlaanderen' },
                  ].map(province => (
                    <li key={province.slug}>
                      <a href={`/${lang}/provincie/${province.slug}`} className="text-gray-500 dark:text-gray-300 hover:text-blue-400 text-sm transition-colors">
                        {province.name}
                      </a>
                    </li>
                  ))}
                </ul>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 mt-6 text-blue-400">
                  {t('navigation.explore', 'Explore')}
                </h4>
                <ul className="space-y-2">
                  <li>
                    <a href={`/${lang}/${chainsPath}`} className="text-gray-500 dark:text-gray-300 hover:text-blue-400 text-sm transition-colors">
                      {t('navigation.chainsCompared', 'Chains compared')}
                    </a>
                  </li>
                  <li>
                    <a href={`/${lang}/${backlogPath}`} className="text-gray-500 dark:text-gray-300 hover:text-blue-400 text-sm transition-colors">
                      {t('navigation.inspectionBacklog', 'Inspection backlog')}
                    </a>
                  </li>
                </ul>
              </div>

              {/* Features Column */}
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-blue-400">
                  {t('navigation.features')}
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-gray-500 dark:text-gray-300 text-sm leading-relaxed">{t('homepage.anonymousReporting')}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span className="text-gray-500 dark:text-gray-300 text-sm leading-relaxed">{t('homepage.officialData')}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="text-gray-500 dark:text-gray-300 text-sm leading-relaxed">{t('homepage.realTimeUpdates')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Always Visible Bottom Bar - Clickable */}
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          className="border-t border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 cursor-pointer"
        >
          <div 
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4"
          >
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0 mr-4">
                <p className="text-gray-500 dark:text-gray-400 text-sm truncate text-left">
                  © {new Date().getFullYear()} Kauwen of Spauwen
                </p>
              </div>
              
              {/* Right side container for social links and expand icon */}
              <div className="flex items-center space-x-3 flex-shrink-0">
                {/* Social Media Links */}
                <a
                  href="https://x.com/KauwenOfSpauwen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Follow us on X (Twitter)"
                >
                  <TwitterIcon className="h-5 w-5" />
                </a>
                <a
                  href="https://www.instagram.com/kauwenofspauwen/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Follow us on Instagram"
                >
                  <InstagramIcon className="h-5 w-5" />
                </a>

                {/* Theme Toggle */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <ThemeToggle variant="footer" />
                </div>

                {/* Expand/Collapse Indicator */}
                <svg
                  className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  if (variant === 'sidebar') {
    return (
      <nav className={`space-y-2 ${className}`}>
        {navigationItems.map((item) => (
          <NavigationLink
            key={item.path}
            to={item.path}
            className="block px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
            activeClassName="bg-blue-50 text-blue-600 border-r-2 border-blue-600"
          >
            {item.label}
          </NavigationLink>
        ))}
      </nav>
    );
  }

  return null;
};

export default Navigation;